import React, { useEffect } from 'react';
import { PmBankAccountSearchResponseDto, InvoiceBankAccountConfigDto } from '@inosio/payments-sdk';
import { Card, Typography, Box, Grid, Divider, Stack, MenuItem, Select } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableBankAccount, getPricingOption, getUpdateInvoiceBankAccount, selectBillingState } from './sagas/billingSlice';
import { useHistory } from 'react-router';
import queryString from 'query-string';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import PricingQuoteConsumablesTable from '../../pricing/components/viewquote/PricingQuoteConsumablesTable';
import PricingQuoteFootNotes from '../../pricing/components/viewquote/PricingQuoteFootNotes';
import PricingQuoteMonthlyFeesTable from '../../pricing/components/viewquote/PricingQuoteMonthlyFeesTable';
import PricingQuoteSummaryCard from '../../pricing/components/viewquote/PricingQuoteSummaryCard';

export default function BillingConfiguration() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pricingOptionResponse, availableBankAccounts, isAvailableBankAccountFetching, availableBankAccountError } = useSelector(selectBillingState);

  const [isUpdateMode, setIsUpdateMode] = React.useState(false);
  React.useEffect(() => {
    const { isUpdateMode } = queryString.parse(window.location.search);
    setIsUpdateMode(!!isUpdateMode);
  }, [history.location.pathname]);

  React.useEffect(() => {
    dispatch({ type: getAvailableBankAccount.type });
    dispatch({ type: getPricingOption.type });
  }, [isUpdateMode]);

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ width: '100%', padding: 4 }}>
        <Grid container rowGap={4}>
          <Grid item xs={12}>
            <PaymentDetailsCard bankAccounts={availableBankAccounts} {...{ isAvailableBankAccountFetching, availableBankAccountError }} />
          </Grid>
          <Grid item xs={12} sx={{ border: '1px solid #F1F0F5', borderRadius: '24px' }}>
            <Typography sx={{ p: 1, pl: 2, fontWeight: '600' }}>{pricingOptionResponse?.SelectedPackageLabel || 'Plan Details'}</Typography>
            <Divider style={{ border: '0.5px solid #F1F0F5' }} />
            <Box padding={4}>
              <Grid container columnSpacing={10} rowSpacing={10}>
                {/* Left Section */}
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', fontSize: '20px', mb: 2 }}>Packages, Products, & Monthly Fees</Typography>
                  <PricingQuoteMonthlyFeesTable includedProducts={pricingOptionResponse?.IncludedProducts || []} packageDiscount={pricingOptionResponse?.SelectedPackageDiscount || 0} />
                </Grid>
                {/* Right Section */}
                <Grid item xs={12} md={6}>
                  <PricingQuoteSummaryCard
                    includedProducts={pricingOptionResponse?.IncludedProducts || []}
                    packageDiscount={pricingOptionResponse?.SelectedPackageDiscount || 0}
                    buttonText="Update My Account"
                    onButtonClick={() => {
                      history.push('/pricing');
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container rowSpacing={5}>
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', fontSize: '20px', mt: 4, mb: 2 }}>Consumables</Typography>
                  <PricingQuoteConsumablesTable includedProducts={pricingOptionResponse?.IncludedProducts || []} />
                </Grid>
                <Grid item xs={12}>
                  <PricingQuoteFootNotes includedProducts={pricingOptionResponse?.IncludedProducts || []} packageDiscount={pricingOptionResponse?.SelectedPackageDiscount || 0} />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
}

function PaymentDetailsCard(props: { bankAccounts: PmBankAccountSearchResponseDto[]; isAvailableBankAccountFetching: boolean; availableBankAccountError: string | null }) {
  const dispatch = useDispatch();
  const storedBankAccount = props?.bankAccounts?.filter((bank) => bank.isSelected)?.[0]?.bankAccountUuid;
  const [selectedBankAccount, setSelectedBankAccount] = React.useState(storedBankAccount ?? '');
  const [firstGuyDisplayText, setFirstGuyDisplayText] = React.useState('select account');

  useEffect(() => {
    const firstBankOnLoad = props.bankAccounts?.find((ba) => !!ba.isSelected)?.last4;
    if (firstBankOnLoad) setFirstGuyDisplayText(firstBankOnLoad);

    const storedBankAccount = props.bankAccounts?.filter((bank) => bank.isSelected)?.[0]?.bankAccountUuid;
    setSelectedBankAccount(storedBankAccount ?? '');
  }, [props.bankAccounts]);

  return (
    <Box sx={{ width: '100%', height: '180px', border: '1px solid #F1F0F5', borderRadius: '24px', '.MuiTypography-root': { fontFamily: 'Work Sans' } }}>
      <Typography sx={{ p: 1, pl: 2, fontWeight: '600' }}>Payment Details</Typography>
      <Divider style={{ border: '0.5px solid #F1F0F5' }} />
      <Box sx={{ height: '75%' }}>
        {props.isAvailableBankAccountFetching ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <SpinnerWithNoConfirmation />
          </Stack>
        ) : !!props.availableBankAccountError ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            <Typography
              style={{
                fontFamily: 'Work Sans',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '18px',
                color: '#DB3939',
                padding: '4%',
              }}
            >
              Unable to fetch bank accounts. Please try again later or contact support.
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography sx={{ p: 2 }}>Charge Date: 1st business day of the month</Typography>
            <Divider style={{ border: '0.5px solid #F1F0F5' }} />
            <Stack direction={'row'}>
              <Typography sx={{ p: 2, width: '80%' }}>Bank Account:</Typography>
              <Select
                value={selectedBankAccount}
                displayEmpty
                sx={{ borderRadius: '24px', height: '31px', m: 2, '.MuiTypography-root': { fontSize: '14px' } }}
                onChange={(e) => {
                  setSelectedBankAccount(e.target.value);
                  dispatch({
                    type: getUpdateInvoiceBankAccount.type,
                    payload: {
                      bankAccountUuid: e.target.value,
                    } as InvoiceBankAccountConfigDto,
                  });
                }}
              >
                <MenuItem disabled value={selectedBankAccount}>
                  <Typography>{firstGuyDisplayText}</Typography>
                </MenuItem>
                {!!props.bankAccounts?.length &&
                  props.bankAccounts
                    .filter((bankAccount) => !bankAccount.isSelected)
                    .map((bankAccount) => {
                      return (
                        <MenuItem value={bankAccount.bankAccountUuid} key={bankAccount.bankAccountUuid}>
                          <Typography>{bankAccount.last4}</Typography>
                        </MenuItem>
                      );
                    })}
              </Select>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
